import React, { useEffect, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { toast } from 'sonner';
import { Building2, Plus, Pencil, Trash2, Power, X, Layers, Check, ShieldCheck, EyeOff, Boxes, History, ArrowRight, Percent, Wallet, Link2, Search, Copy, Package, Save } from 'lucide-react';

const PRACTICE_TYPES = [
  { value: 'physio', label: 'Physiotherapie' },
  { value: 'orthopaede', label: 'Orthopädie' },
  { value: 'fitness', label: 'Fitness / Studio' },
  { value: 'beauty', label: 'Beauty / Kosmetik' },
  { value: 'andere', label: 'Andere' },
];

const COND_TYPES = [
  { value: 'percent_total', label: 'Rabatt % aufs ganze Abo', unit: '%', needsModule: false },
  { value: 'percent_module', label: 'Rabatt % auf eine Funktion', unit: '%', needsModule: true },
  { value: 'special_price', label: 'Sonderpreis (fixer Monatsbetrag)', unit: 'CHF', needsModule: false },
  { value: 'free_months', label: 'Gratismonat(e)', unit: 'Monate', needsModule: false },
  { value: 'free_module', label: 'Funktion gratis', unit: '', needsModule: true },
  { value: 'waive_setup', label: 'Einrichtungsgebühr erlassen', unit: '', needsModule: false },
  { value: 'credit', label: 'Gutschrift (einmalig)', unit: 'CHF', needsModule: false },
  { value: 'extra_credits', label: 'Zusätzliche KI-Credits', unit: 'Credits', needsModule: false },
];
const COND_EMPTY = { type: 'percent_total', value: '', module_key: '', starts_at: '', ends_at: '', reason: '' };

const EMPTY = {
  name: '', practice_type: 'physio', contact_email: '', contact_phone: '', city: '',
  email_from_name: '', email_from_email: '', brand_primary_color: '', logo_url: '',
  medidata_sender_gln: '', status: 'active', notes: '',
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium text-slate-600">{label}</span>
    {children}
  </label>
);

const inputCls = 'w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // tenant object or {} for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Funktionskatalog pro Mandant
  const [modTenant, setModTenant] = useState(null);
  const [modData, setModData] = useState(null);
  const [modLoading, setModLoading] = useState(false);
  const [modSaving, setModSaving] = useState(false);
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // Individuelle Konditionen
  const [condTenant, setCondTenant] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [billing, setBilling] = useState(null);
  const [condLoading, setCondLoading] = useState(false);
  const [condForm, setCondForm] = useState(COND_EMPTY);
  const [condSaving, setCondSaving] = useState(false);
  // Studio-Lizenz: Sonderpreis / Dauerrabatt je Mandant
  const [studioOv, setStudioOv] = useState(null);
  const [studioBase, setStudioBase] = useState(null);
  const [studioOvSaving, setStudioOvSaving] = useState(false);

  const load = () => {
    setLoading(true);
    superAdminApi.listTenants()
      .then((r) => setTenants(r.data || []))
      .catch(() => toast.error('Mandanten konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (t) => { setForm({ ...EMPTY, ...t }); setEditing(t); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const copyStudioId = (t) => {
    const sid = String(t.studio_id || '');
    navigator.clipboard?.writeText(sid)
      .then(() => toast.success(`Studio-ID ${sid} kopiert`))
      .catch(() => {});
  };

  const studioLink = (t, suffix) => `${window.location.origin}/${t.slug}/${suffix}`;
  const copyLink = (t, suffix, label) => {
    navigator.clipboard?.writeText(studioLink(t, suffix))
      .then(() => toast.success(`${label}-Link kopiert`))
      .catch(() => {});
  };

  const q = query.trim().toLowerCase();
  const filtered = !q ? tenants : tenants.filter((t) =>
    String(t.studio_id || '').includes(q)
    || (t.name || '').toLowerCase().includes(q)
    || (t.slug || '').toLowerCase().includes(q));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name ist erforderlich'); return; }
    setSaving(true);
    try {
      if (editing && editing.id) {
        await superAdminApi.updateTenant(editing.id, form);
        toast.success('Mandant aktualisiert');
      } else {
        await superAdminApi.createTenant(form);
        toast.success('Mandant angelegt');
      }
      close();
      load();
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (t) => {
    const next = t.status === 'active' ? 'inactive' : 'active';
    try { await superAdminApi.setTenantStatus(t.id, next); load(); }
    catch { toast.error('Status konnte nicht geändert werden'); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Mandant "${t.name}" wirklich löschen?`)) return;
    try { await superAdminApi.deleteTenant(t.id); toast.success('Gelöscht'); load(); }
    catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const openModules = async (t) => {
    setModTenant(t);
    setModData(null);
    setHistory([]);
    setShowHistory(false);
    setModLoading(true);
    try {
      const [r, p, h] = await Promise.all([
        superAdminApi.getTenantModules(t.id),
        packages.length ? Promise.resolve({ data: { packages } }) : superAdminApi.getModulePackages(),
        superAdminApi.getTenantModulesHistory(t.id),
      ]);
      setModData(r.data);
      setPackages(p.data.packages || []);
      setHistory(h.data.entries || []);
    } catch {
      toast.error('Funktionskatalog konnte nicht geladen werden');
      setModTenant(null);
    } finally { setModLoading(false); }
  };
  const closeModules = () => { setModTenant(null); setModData(null); };

  const refreshHistory = async (tid) => {
    try {
      const h = await superAdminApi.getTenantModulesHistory(tid);
      setHistory(h.data.entries || []);
    } catch { /* still */ }
  };

  const toggleModule = (key) => {
    setModData((d) => {
      const target = d.modules.find((m) => m.key === key);
      const turningOn = target && !target.enabled;
      // Abhängigkeitsgraph: beim Aktivieren Pflichtmodule rekursiv mit einschalten.
      const toEnable = new Set();
      if (turningOn) {
        const byKey = Object.fromEntries(d.modules.map((m) => [m.key, m]));
        const stack = [key];
        while (stack.length) {
          const k = stack.pop();
          if (toEnable.has(k)) continue;
          toEnable.add(k);
          (byKey[k]?.requires || []).forEach((r) => { if (byKey[r] && !byKey[r].enabled) stack.push(r); });
        }
        const auto = [...toEnable].filter((k) => k !== key && !byKey[k].enabled);
        if (auto.length) toast.info(`Automatisch mit aktiviert: ${auto.map((k) => byKey[k].name).join(', ')}`);
      }
      return {
        ...d,
        modules: d.modules.map((m) => {
          if (m.key === key) return { ...m, enabled: !m.enabled };
          if (turningOn && toEnable.has(m.key)) return { ...m, enabled: true };
          return m;
        }),
      };
    });
  };
  const toggleAllModules = () => {
    setModData((d) => {
      const next = !d.all_modules_active;
      return {
        ...d,
        all_modules_active: next,
        modules: next ? d.modules.map((m) => ({ ...m, enabled: true })) : d.modules,
      };
    });
  };

  const applyPackage = async (pkg) => {
    if (!modTenant) return;
    setModSaving(true);
    try {
      const r = await superAdminApi.setTenantModules(modTenant.id, {
        all_modules_active: false,
        module_keys: pkg.module_keys,
        package: pkg.name,
      });
      setModData(r.data);
      await refreshHistory(modTenant.id);
      toast.success(`Paket „${pkg.name}" freigeschaltet`);
    } catch {
      toast.error('Paket konnte nicht freigeschaltet werden');
    } finally { setModSaving(false); }
  };

  const saveModules = async () => {
    setModSaving(true);
    try {
      const keys = modData.modules.filter((m) => m.enabled).map((m) => m.key);
      const r = await superAdminApi.setTenantModules(modTenant.id, {
        all_modules_active: modData.all_modules_active,
        module_keys: keys,
      });
      setModData(r.data);
      await refreshHistory(modTenant.id);
      const auto = r.data.auto_added || [];
      toast.success(`Funktionen gespeichert${auto.length ? ` · ${auto.length} Pflichtmodul(e) ergänzt` : ''}`);
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally { setModSaving(false); }
  };

  const openConditions = async (t) => {
    setCondTenant(t);
    setConditions([]);
    setBilling(null);
    setCondForm(COND_EMPTY);
    setCondLoading(true);
    try {
      const [c, b, so] = await Promise.all([
        superAdminApi.getTenantConditions(t.id),
        superAdminApi.getTenantBillingPreview(t.id),
        superAdminApi.getStudioTenantOverride(t.id),
      ]);
      setConditions(c.data.conditions || []);
      setBilling(b.data);
      setStudioOv(so.data.override);
      setStudioBase(so.data.base);
    } catch {
      toast.error('Konditionen konnten nicht geladen werden');
      setCondTenant(null);
    } finally { setCondLoading(false); }
  };
  const closeConditions = () => { setCondTenant(null); setConditions([]); setBilling(null); setStudioOv(null); setStudioBase(null); };

  const saveStudioOverride = async () => {
    if (!condTenant || !studioOv) return;
    setStudioOvSaving(true);
    try {
      const r = await superAdminApi.setStudioTenantOverride(condTenant.id, {
        mode: studioOv.mode,
        percent: parseFloat(studioOv.percent) || 0,
        monthly_chf: parseFloat(studioOv.monthly_chf) || 0,
        yearly_chf: parseFloat(studioOv.yearly_chf) || 0,
        reason: studioOv.reason || '',
        valid_until: studioOv.valid_until || '',
        notify: studioOv.notify !== false,
      });
      setStudioOv(r.data.override);
      toast.success('Studio-Lizenz-Sonderpreis gespeichert' + (studioOv.notify !== false && studioOv.mode !== 'none' ? ' · Mandant wird informiert' : ''));
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setStudioOvSaving(false); }
  };

  const refreshBilling = async (tid) => {
    try {
      const [c, b] = await Promise.all([
        superAdminApi.getTenantConditions(tid),
        superAdminApi.getTenantBillingPreview(tid),
      ]);
      setConditions(c.data.conditions || []);
      setBilling(b.data);
    } catch { /* still */ }
  };

  const addCondition = async () => {
    const def = COND_TYPES.find((x) => x.value === condForm.type);
    if (!condForm.reason.trim()) { toast.error('Grund ist Pflicht'); return; }
    if (def?.needsModule && !condForm.module_key) { toast.error('Bitte eine Funktion wählen'); return; }
    setCondSaving(true);
    try {
      await superAdminApi.addTenantCondition(condTenant.id, {
        type: condForm.type,
        value: parseFloat(condForm.value) || 0,
        module_key: def?.needsModule ? condForm.module_key : null,
        starts_at: condForm.starts_at || null,
        ends_at: condForm.ends_at || null,
        reason: condForm.reason.trim(),
      });
      setCondForm(COND_EMPTY);
      await refreshBilling(condTenant.id);
      toast.success('Kondition hinzugefügt');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Hinzufügen fehlgeschlagen');
    } finally { setCondSaving(false); }
  };

  const removeCondition = async (cid) => {
    try {
      await superAdminApi.deleteTenantCondition(condTenant.id, cid);
      await refreshBilling(condTenant.id);
      toast.success('Kondition entfernt');
    } catch { toast.error('Entfernen fehlgeschlagen'); }
  };

  return (
    <div data-testid="sa-tenants" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Mandanten</h1>
          <p className="mt-1 text-sm text-slate-600">Praxen & Studios, die StrongFit nutzen.</p>
        </div>
        <button data-testid="sa-add-tenant-btn" onClick={openNew}
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-400">
          <Plus className="h-4 w-4" /> Mandant anlegen
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="sa-tenant-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Suche nach Studio-ID (z.B. 1000) oder Name…"
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>

      {loading ? (
        <div className="text-slate-600">Lädt…</div>
      ) : tenants.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-12 text-center">
          <Building2 className="mx-auto mb-3 h-8 w-8 text-slate-400" />
          <p className="text-slate-700">Noch keine Mandanten.</p>
          <p className="text-sm text-slate-500">Lege die erste Praxis an, um sie auf die Plattform zu holen.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-12 text-center" data-testid="sa-tenant-no-match">
          <Search className="mx-auto mb-3 h-8 w-8 text-slate-400" />
          <p className="text-slate-700">Kein Studio gefunden für „{query}".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {filtered.map((t) => (
            <div key={t.id} data-testid={`sa-tenant-card-${t.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{t.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${t.status === 'active' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {t.status === 'active' ? 'aktiv' : 'inaktiv'}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {PRACTICE_TYPES.find((p) => p.value === t.practice_type)?.label || t.practice_type}
                    {t.city ? ` · ${t.city}` : ''}
                  </div>
                </div>
                {t.studio_id != null && (
                  <button
                    type="button"
                    data-testid={`sa-tenant-studio-id-${t.id}`}
                    onClick={() => copyStudioId(t)}
                    title="Studio-ID kopieren"
                    className="group inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 font-mono text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100">
                    Studio-ID: {t.studio_id}
                    <Copy className="h-3 w-3 text-indigo-400 group-hover:text-indigo-600" />
                  </button>
                )}
              </div>
              <div className="mt-3 space-y-1 text-sm text-slate-600">
                {t.contact_email && <div>{t.contact_email}</div>}
                {t.email_from_email && <div className="text-xs text-slate-500">Absender: {t.email_from_email}</div>}
                {t.medidata_sender_gln && <div className="text-xs text-slate-500">GLN: {t.medidata_sender_gln}</div>}
              </div>

              {/* Fertige Studio-Links zum Weitergeben */}
              <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Links zum Weitergeben</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {[
                    { suffix: 'admin', label: 'Team / Admin', testid: 'admin' },
                    { suffix: 'portal', label: 'Kundenportal', testid: 'portal' },
                  ].map((lnk) => (
                    <button
                      key={lnk.suffix}
                      type="button"
                      data-testid={`sa-tenant-link-${lnk.testid}-${t.id}`}
                      onClick={() => copyLink(t, lnk.suffix, lnk.label)}
                      title={studioLink(t, lnk.suffix)}
                      className="group flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-left transition-colors hover:border-indigo-300 hover:bg-indigo-50">
                      <span className="min-w-0">
                        <span className="block text-[11px] font-medium text-slate-500">{lnk.label}</span>
                        <span className="block truncate font-mono text-xs text-slate-700">/{t.slug}/{lnk.suffix}</span>
                      </span>
                      <Copy className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-indigo-600" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <button data-testid={`sa-edit-tenant-${t.id}`} onClick={() => openEdit(t)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-800 hover:bg-slate-100">
                  <Pencil className="h-3.5 w-3.5" /> Bearbeiten
                </button>
                <button data-testid={`sa-modules-tenant-${t.id}`} onClick={() => openModules(t)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-800 hover:bg-slate-100">
                  <Layers className="h-3.5 w-3.5" /> Funktionen
                </button>
                <button data-testid={`sa-conditions-tenant-${t.id}`} onClick={() => openConditions(t)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-800 hover:bg-slate-100">
                  <Percent className="h-3.5 w-3.5" /> Konditionen
                </button>
                <button data-testid={`sa-toggle-tenant-${t.id}`} onClick={() => toggleStatus(t)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-800 hover:bg-slate-100">
                  <Power className="h-3.5 w-3.5" /> {t.status === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                </button>
                <button data-testid={`sa-delete-tenant-${t.id}`} onClick={() => remove(t)}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={close}>
          <div data-testid="sa-tenant-dialog" className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">{editing.id ? 'Mandant bearbeiten' : 'Neuer Mandant'}</h2>
              <button onClick={close} className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Name der Praxis *">
                <input data-testid="sa-field-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. Physio Rapperswil" />
              </Field>
              <Field label="Typ">
                <select data-testid="sa-field-type" className={inputCls} value={form.practice_type} onChange={(e) => setForm({ ...form, practice_type: e.target.value })}>
                  {PRACTICE_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="Kontakt E-Mail">
                <input className={inputCls} value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
              </Field>
              <Field label="Telefon">
                <input className={inputCls} value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
              </Field>
              <Field label="Ort">
                <input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </Field>
              <Field label="MediData Absender-GLN">
                <input className={inputCls} value={form.medidata_sender_gln} onChange={(e) => setForm({ ...form, medidata_sender_gln: e.target.value })} />
              </Field>
              <Field label="Eigener E-Mail-Absender (Name)">
                <input className={inputCls} value={form.email_from_name} onChange={(e) => setForm({ ...form, email_from_name: e.target.value })} />
              </Field>
              <Field label="Eigener E-Mail-Absender (Adresse)">
                <input className={inputCls} value={form.email_from_email} onChange={(e) => setForm({ ...form, email_from_email: e.target.value })} />
              </Field>
              <Field label="Markenfarbe (Hex)">
                <input className={inputCls} value={form.brand_primary_color} onChange={(e) => setForm({ ...form, brand_primary_color: e.target.value })} placeholder="#0ea5a3" />
              </Field>
              <Field label="Logo-URL">
                <input className={inputCls} value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} />
              </Field>
            </div>
            <Field label="Notizen">
              <textarea className={`${inputCls} mt-0`} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={close} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100">Abbrechen</button>
              <button data-testid="sa-save-tenant-btn" onClick={save} disabled={saving}
                className="rounded-xl bg-indigo-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50">
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={closeModules}>
          <div data-testid="sa-modules-dialog" className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 p-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Funktionen · {modTenant.name}</h2>
                <p className="mt-0.5 text-sm text-slate-600">
                  {modData ? `${modData.enabled_count} von ${modData.total_count} Modulen aktiv` : 'Lädt…'}
                </p>
              </div>
              <button onClick={closeModules} className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            {modLoading || !modData ? (
              <div className="p-12 text-center text-slate-600">Lädt…</div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-6">
                  {/* Paket-Vorlagen: Ein-Klick-Freigabe */}
                  {packages.length > 0 && (
                    <div className="mb-5" data-testid="sa-packages">
                      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <Boxes className="h-4 w-4" /> Paket-Vorlagen
                      </div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                        {packages.map((p) => (
                          <button
                            key={p.key}
                            data-testid={`sa-package-${p.key}`}
                            onClick={() => applyPackage(p)}
                            disabled={modSaving}
                            title={p.description}
                            className="group flex flex-col rounded-xl border border-slate-200 bg-slate-50 p-3 text-left transition-colors hover:border-indigo-300 hover:bg-indigo-50 disabled:opacity-50">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-slate-900">{p.name}</span>
                              <ArrowRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-indigo-600" />
                            </div>
                            <span className="mt-0.5 text-xs text-slate-500">{p.module_keys.length} Module · CHF {p.price_chf}/Mt.</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    data-testid="sa-modules-all-toggle"
                    onClick={toggleAllModules}
                    className={`mb-5 flex w-full items-center justify-between rounded-xl border p-4 text-left transition-colors ${modData.all_modules_active ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-slate-200 bg-slate-50'}`}>
                    <div className="flex items-center gap-3">
                      <ShieldCheck className={`h-5 w-5 ${modData.all_modules_active ? 'text-emerald-600' : 'text-slate-500'}`} />
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Alle Module (Sonderfreigabe)</div>
                        <div className="text-xs text-slate-600">Schaltet den vollen Funktionsumfang frei. Einzelne Module unten können gezielt ausgeschlossen werden (z. B. branchenfremde).</div>
                      </div>
                    </div>
                    <span className={`h-6 w-11 rounded-full p-0.5 transition-colors ${modData.all_modules_active ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                      <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${modData.all_modules_active ? 'translate-x-5' : ''}`} />
                    </span>
                  </button>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {modData.modules.map((m) => (
                      <button
                        key={m.key}
                        data-testid={`sa-module-toggle-${m.key}`}
                        onClick={() => toggleModule(m.key)}
                        className={`flex items-center justify-between rounded-xl border p-3 text-left transition-colors ${m.enabled ? 'border-indigo-500/50 bg-indigo-500/10' : 'border-slate-200 bg-slate-50'}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-sm font-medium text-slate-900">{m.name}</span>
                            {!m.customer_visible && <EyeOff className="h-3 w-3 shrink-0 text-slate-500" title="Versteckt (nicht kundensichtbar)" />}
                          </div>
                          <div className="text-xs text-slate-500">{m.key} · CHF {m.price_chf}/Mt.</div>
                          {m.requires && m.requires.length > 0 && (
                            <div className="mt-0.5 flex items-center gap-1 text-[11px] text-amber-600">
                              <Link2 className="h-3 w-3 shrink-0" />
                              benötigt {m.requires.map((rk) => (modData.modules.find((x) => x.key === rk)?.name) || rk).join(', ')}
                            </div>
                          )}
                        </div>
                        <span className={`ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${m.enabled ? 'border-indigo-400 bg-indigo-500 text-white' : 'border-slate-300'}`}>
                          {m.enabled && <Check className="h-3.5 w-3.5" />}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Funktions-Verlauf */}
                  <div className="mt-6 border-t border-slate-200 pt-4">
                    <button
                      data-testid="sa-history-toggle"
                      onClick={() => setShowHistory((v) => !v)}
                      className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900">
                      <History className="h-4 w-4" />
                      Funktions-Verlauf {history.length > 0 && <span className="text-slate-400">({history.length})</span>}
                      <ArrowRight className={`h-3.5 w-3.5 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
                    </button>
                    {showHistory && (
                      <div className="mt-3 space-y-2" data-testid="sa-history-list">
                        {history.length === 0 ? (
                          <p className="text-sm text-slate-500">Noch keine Änderungen protokolliert.</p>
                        ) : history.map((h) => (
                          <div key={h.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                            <div className="flex flex-wrap items-center justify-between gap-1">
                              <span className="font-medium text-slate-800">{h.actor_email || 'System'}</span>
                              <span className="text-xs text-slate-500">{new Date(h.created_at).toLocaleString('de-CH')}</span>
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                              {h.package && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                                  <Boxes className="h-3 w-3" /> Paket „{h.package}"
                                </span>
                              )}
                              {h.all_modules_active && (
                                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Alle Module</span>
                              )}
                              {h.added.map((a) => (
                                <span key={`a-${a.key}`} className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 ring-1 ring-emerald-200">+ {a.name}</span>
                              ))}
                              {h.removed.map((r) => (
                                <span key={`r-${r.key}`} className="rounded-full bg-rose-50 px-2 py-0.5 text-xs text-rose-700 ring-1 ring-rose-200">− {r.name}</span>
                              ))}
                              {!h.package && !h.all_modules_active && h.added.length === 0 && h.removed.length === 0 && (
                                <span className="text-xs text-slate-500">Keine Modul-Änderung</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 border-t border-slate-200 p-5">
                  <button onClick={closeModules} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100">Schließen</button>
                  <button data-testid="sa-modules-save-btn" onClick={saveModules} disabled={modSaving}
                    className="rounded-xl bg-indigo-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50">
                    {modSaving ? 'Speichert…' : 'Funktionen speichern'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {condTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={closeConditions}>
          <div data-testid="sa-conditions-dialog" className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 p-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Konditionen · {condTenant.name}</h2>
                <p className="mt-0.5 text-sm text-slate-600">Individuelle Rabatte, Sonderpreise & Gratismonate</p>
              </div>
              <button onClick={closeConditions} className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            {condLoading ? (
              <div className="p-12 text-center text-slate-600">Lädt…</div>
            ) : (
              <div className="flex-1 overflow-y-auto p-6">
                {/* Studio-Lizenz: Sonderpreis / Dauerrabatt */}
                {studioOv && (
                  <div data-testid="sa-studio-override" className="mb-6 rounded-2xl border border-slate-200 p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900"><Package className="h-4 w-4" /> Studio-Lizenz · Sonderpreis / Dauerrabatt</div>
                    <p className="mb-3 text-xs text-slate-500">Standardpreis: CHF {Number(studioBase?.monthly_chf || 0).toFixed(2)}/Mt · CHF {Number(studioBase?.yearly_chf || 0).toFixed(2)}/Jahr. Gilt dauerhaft für jede zusätzliche Studio-Lizenz dieses Mandanten.</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field label="Modus">
                        <select data-testid="sa-studio-override-mode" className={inputCls} value={studioOv.mode}
                          onChange={(e) => setStudioOv({ ...studioOv, mode: e.target.value })}>
                          <option value="none">Standardpreis (kein Sonderpreis)</option>
                          <option value="percent">Dauerrabatt in %</option>
                          <option value="fixed">Fester Sonderpreis (CHF)</option>
                        </select>
                      </Field>
                      {studioOv.mode === 'percent' && (
                        <Field label="Rabatt (%)">
                          <input data-testid="sa-studio-override-percent" type="number" className={inputCls} value={studioOv.percent}
                            onChange={(e) => setStudioOv({ ...studioOv, percent: e.target.value })} placeholder="z.B. 20" />
                        </Field>
                      )}
                      {studioOv.mode === 'fixed' && (
                        <>
                          <Field label="Sonderpreis / Monat (CHF)">
                            <input data-testid="sa-studio-override-monthly" type="number" className={inputCls} value={studioOv.monthly_chf}
                              onChange={(e) => setStudioOv({ ...studioOv, monthly_chf: e.target.value })} />
                          </Field>
                          <Field label="Sonderpreis / Jahr (CHF)">
                            <input data-testid="sa-studio-override-yearly" type="number" className={inputCls} value={studioOv.yearly_chf}
                              onChange={(e) => setStudioOv({ ...studioOv, yearly_chf: e.target.value })} />
                          </Field>
                        </>
                      )}
                      <div className="sm:col-span-2">
                        <Field label="Grund (optional)">
                          <input data-testid="sa-studio-override-reason" className={inputCls} value={studioOv.reason}
                            onChange={(e) => setStudioOv({ ...studioOv, reason: e.target.value })} placeholder="z.B. Partnerstudio" />
                        </Field>
                      </div>
                      {studioOv.mode !== 'none' && (
                        <>
                          <Field label="Gültig bis (optional)">
                            <input data-testid="sa-studio-override-valid-until" type="date" className={inputCls} value={studioOv.valid_until || ''}
                              onChange={(e) => setStudioOv({ ...studioOv, valid_until: e.target.value })} />
                          </Field>
                          <label className="flex items-end gap-2 pb-2 text-xs text-slate-600">
                            <input data-testid="sa-studio-override-notify" type="checkbox" checked={studioOv.notify !== false}
                              onChange={(e) => setStudioOv({ ...studioOv, notify: e.target.checked })} />
                            Mandant per E-Mail informieren
                          </label>
                        </>
                      )}
                    </div>
                    {studioOv.valid_until && (() => {
                      const expiredNow = studioOv.valid_until < new Date().toISOString().slice(0, 10);
                      return (
                        <div className={`mt-3 text-xs ${expiredNow ? 'text-rose-600' : 'text-emerald-600'}`} data-testid="sa-studio-override-status">
                          {expiredNow
                            ? `Abgelaufen am ${studioOv.valid_until} — es gilt wieder der Standardpreis.`
                            : `Sonderkondition gültig bis ${studioOv.valid_until}.`}
                        </div>
                      );
                    })()}
                    <div className="mt-3 flex justify-end">
                      <button data-testid="sa-studio-override-save" onClick={saveStudioOverride} disabled={studioOvSaving}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50">
                        <Save className="h-4 w-4" /> {studioOvSaving ? 'Speichert…' : 'Speichern'}
                      </button>
                    </div>
                  </div>
                )}
                {/* Abrechnungs-Vorschau */}
                {billing && (
                  <div data-testid="sa-billing-preview" className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <Wallet className="h-4 w-4" /> Abrechnungs-Vorschau (Monat)
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
                        <div className="text-xs text-slate-500">Listenpreis</div>
                        <div data-testid="sa-billing-base" className="mt-0.5 text-lg font-semibold text-slate-900">CHF {billing.base_total.toFixed(2)}</div>
                      </div>
                      <div className="rounded-xl bg-white p-3 ring-1 ring-rose-100">
                        <div className="text-xs text-slate-500">Rabatt</div>
                        <div data-testid="sa-billing-discount" className="mt-0.5 text-lg font-semibold text-rose-600">− CHF {billing.monthly_discount.toFixed(2)}</div>
                      </div>
                      <div className="rounded-xl bg-white p-3 ring-1 ring-emerald-200">
                        <div className="text-xs text-slate-500">Netto / Mt.</div>
                        <div data-testid="sa-billing-net" className="mt-0.5 text-lg font-semibold text-emerald-700">CHF {billing.net_total.toFixed(2)}</div>
                      </div>
                    </div>
                    {billing.month_free && (
                      <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">Dieser Monat ist gratis (Gratismonat aktiv)</div>
                    )}
                    {(billing.extra_credits > 0 || billing.setup_waived) && (
                      <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                        {billing.extra_credits > 0 && <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-medium text-indigo-700">+{billing.extra_credits} KI-Credits</span>}
                        {billing.setup_waived && <span className="rounded-full bg-slate-200 px-2 py-0.5 font-medium text-slate-700">Einrichtungsgebühr erlassen</span>}
                      </div>
                    )}
                    {billing.applied && billing.applied.length > 0 && (
                      <ul className="mt-3 space-y-1 text-xs text-slate-600">
                        {billing.applied.map((a, i) => (
                          <li key={i} className="flex items-center justify-between gap-2">
                            <span>{a.label}{a.reason ? ` · ${a.reason}` : ''}</span>
                            {a.effect != null && <span className="font-medium text-rose-600">CHF {a.effect.toFixed(2)}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Neue Kondition */}
                <div className="mb-6 rounded-2xl border border-slate-200 p-4">
                  <div className="mb-3 text-sm font-semibold text-slate-900">Neue Kondition</div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Art">
                      <select data-testid="sa-cond-type" className={inputCls} value={condForm.type}
                        onChange={(e) => setCondForm({ ...condForm, type: e.target.value, module_key: '' })}>
                        {COND_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </Field>
                    {(() => {
                      const def = COND_TYPES.find((x) => x.value === condForm.type);
                      if (def?.value === 'waive_setup') return <div className="hidden sm:block" />;
                      if (def?.needsModule && def?.value === 'free_module') {
                        return (
                          <Field label="Funktion">
                            <select data-testid="sa-cond-module" className={inputCls} value={condForm.module_key}
                              onChange={(e) => setCondForm({ ...condForm, module_key: e.target.value })}>
                              <option value="">– wählen –</option>
                              {(billing?.positions || []).map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                            </select>
                          </Field>
                        );
                      }
                      return (
                        <Field label={`Wert${def?.unit ? ` (${def.unit})` : ''}`}>
                          <input data-testid="sa-cond-value" type="number" className={inputCls} value={condForm.value}
                            onChange={(e) => setCondForm({ ...condForm, value: e.target.value })} placeholder="z.B. 15" />
                        </Field>
                      );
                    })()}
                    {COND_TYPES.find((x) => x.value === condForm.type)?.value === 'percent_module' && (
                      <Field label="Funktion">
                        <select data-testid="sa-cond-module" className={inputCls} value={condForm.module_key}
                          onChange={(e) => setCondForm({ ...condForm, module_key: e.target.value })}>
                          <option value="">– wählen –</option>
                          {(billing?.positions || []).map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                        </select>
                      </Field>
                    )}
                    <Field label="Gültig ab (optional)">
                      <input data-testid="sa-cond-start" type="date" className={inputCls} value={condForm.starts_at}
                        onChange={(e) => setCondForm({ ...condForm, starts_at: e.target.value })} />
                    </Field>
                    <Field label="Gültig bis (optional)">
                      <input data-testid="sa-cond-end" type="date" className={inputCls} value={condForm.ends_at}
                        onChange={(e) => setCondForm({ ...condForm, ends_at: e.target.value })} />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Grund *">
                        <input data-testid="sa-cond-reason" className={inputCls} value={condForm.reason}
                          onChange={(e) => setCondForm({ ...condForm, reason: e.target.value })} placeholder="z.B. Neukundenrabatt 6 Monate" />
                      </Field>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button data-testid="sa-cond-add-btn" onClick={addCondition} disabled={condSaving}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50">
                      <Plus className="h-4 w-4" /> {condSaving ? 'Fügt hinzu…' : 'Kondition hinzufügen'}
                    </button>
                  </div>
                </div>

                {/* Laufende Konditionen */}
                <div>
                  <div className="mb-2 text-sm font-semibold text-slate-900">Laufende Konditionen</div>
                  {conditions.length === 0 ? (
                    <p className="text-sm text-slate-500" data-testid="sa-cond-empty">Noch keine individuellen Konditionen hinterlegt.</p>
                  ) : (
                    <div className="space-y-2" data-testid="sa-cond-list">
                      {conditions.map((c) => (
                        <div key={c.id} data-testid={`sa-cond-item-${c.id}`} className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-sm font-medium text-slate-900">{c.type_label}</span>
                              {c.module_key && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[11px] text-slate-700">{c.module_key}</span>}
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${c.active_now ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                                {c.active_now ? 'aktiv' : 'inaktiv'}
                              </span>
                            </div>
                            <div className="mt-0.5 text-xs text-slate-500">
                              Wert: {c.value} · {c.reason}
                              {(c.starts_at || c.ends_at) && ` · ${c.starts_at || 'sofort'} → ${c.ends_at || 'unbefristet'}`}
                            </div>
                            <div className="text-[11px] text-slate-400">von {c.created_by}</div>
                          </div>
                          <button data-testid={`sa-cond-delete-${c.id}`} onClick={() => removeCondition(c.id)}
                            className="shrink-0 rounded-lg border border-rose-200 p-1.5 text-rose-600 hover:bg-rose-50">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
